import { useEffect } from "react";
import { useMap } from "maplibre-react-components";
import type { Map as MapLibreMap } from "maplibre-gl";
import { useAgglomeration } from "@/store/agglomeration";
import { useSelectedHexGroupStore } from "@/store/selectedHexGroup";
import { changeUserHexGroupId, changeUserAgglomerationId } from "@/api/user";
import { useAgglomerationsDataContext } from "@/store/AgglomerationsProvider";

export const useSelectedHexGroupByPin = (mapId: string) => {
  const map = useMap(mapId) as MapLibreMap | null;

  const { agglomerations } = useAgglomerationsDataContext();
  const {
    selectedAgglomeration,
    setSelectedAgglomeration,
  } = useAgglomeration();

  const selectedHexGroup = useSelectedHexGroupStore((s) => s.selectedHexGroup);
  const setSelectedHexGroup = useSelectedHexGroupStore(
    (s) => s.setSelectedHexGroup
  );
  const setCurrentHexGroup = useSelectedHexGroupStore(
    (s) => s.setCurrentHexGroup
  );

  const updateAgglomeration = () => {
    if (!map || !map.isStyleLoaded()) return;
    if (!map.getLayer("hex-fill")) return;

    const centerPoint = map.project(map.getCenter());

    const features = map.queryRenderedFeatures([centerPoint.x, centerPoint.y], {
      layers: ["hex-fill"],
    });

    const hexGroupId = features[0]?.properties?.hexgroup_id;

    const nextAgglomerationGroup = agglomerations?.find((item) =>
      item.hexgroups?.some((hexgroup) => hexgroup.id === hexGroupId)
    );
    const nextHexGroup = nextAgglomerationGroup?.hexgroups.find(
      (hg) => hg.id === Number(hexGroupId)
    );

    if (!nextHexGroup || !nextAgglomerationGroup) {
      if (selectedHexGroup !== null) {
        setCurrentHexGroup(selectedHexGroup);
        setSelectedHexGroup(null);
      }
      return;
    }

    if (selectedAgglomeration?.id !== nextAgglomerationGroup?.id) {
      setSelectedAgglomeration(nextAgglomerationGroup);
      changeUserAgglomerationId({ agglomerationId: nextAgglomerationGroup.id });
    }

    if (selectedHexGroup?.id !== nextHexGroup?.id) {
      setSelectedHexGroup(nextHexGroup);
      changeUserHexGroupId({ hexGroupId: nextHexGroup.id });
    }
  };

  useEffect(() => {
    if (!map) return;

    map.on("move", updateAgglomeration);
    map.on("load", updateAgglomeration);
    map.on("dragend", updateAgglomeration);

    updateAgglomeration();

    return () => {
      map.off("move", updateAgglomeration);
      map.off("load", updateAgglomeration);
      map.off("dragend", updateAgglomeration);
    };
  }, [map, selectedAgglomeration, selectedHexGroup]);
};
